import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useRef } from 'react';
import SnapPeas from '@assets/icons/snap-peas-image.svg';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Analytics } from '@/utils/analytics';
import { ChatStatus } from '@/enums';
import { createStyles } from '@/contexts/theme-context';
import { EmployeeRestaurant, useUserContext } from '@/contexts/user-context';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { getSearchableFields } from '@/hooks/use-location-picker-data';
import { InteractionManager, Platform, Text, View } from 'react-native';
import { LIST_MARGIN_HORIZONTAL, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { Props as ListItemProps, ListOption } from '@/components/list-item';
import { RecyclerList, RecyclerListRef } from '@/components/recycler-list';
import { SearchInput } from '@/components/search-input';
import { useMultiSearchFilters } from '@/hooks/use-multi-search-filters';

type AccountOption = ListOption<{
  isSelected: boolean;
}>;

interface Props {
  activeRestaurantUUID?: string;
  onSelect: (restaurantRelationship: EmployeeRestaurant) => void;
  skipInteractionCheck?: boolean;
}

export const AccountPickerList: React.FC<Props> = ({
  activeRestaurantUUID,
  onSelect,
  skipInteractionCheck = false,
}) => {
  const [styles, theme] = useStyles();
  const { restaurantRelationships, restaurantUUIDToRelationshipMap } =
    useUserContext();
  const scrolledToSelectedIndexRef = useRef(false);
  const listRef = useRef<RecyclerListRef<AccountOption>>(null);

  // Exclude prospect and inactive chats from the inbounds account picker.
  const selectableRelationships = useMemo(
    () =>
      restaurantRelationships.filter(
        (relationship) =>
          relationship.chatStatus !== ChatStatus.PROSPECT &&
          relationship.chatStatus !== ChatStatus.INACTIVE,
      ),
    [restaurantRelationships],
  );

  const { filteredData, onDisplayTextChange, searchText, searching } =
    useMultiSearchFilters<EmployeeRestaurant>({
      data: selectableRelationships,
      getSearchableFields,
    });

  const sectionData = useMemo(() => {
    const data: AccountOption[] = filteredData.map((relationship) => ({
      metadata: {
        isSelected: relationship.uuid === activeRestaurantUUID,
      },
      subtitle: relationship.addressString,
      title: relationship.name,
      value: relationship.uuid,
    }));
    return data;
  }, [filteredData, activeRestaurantUUID]);

  const listItemProps: Partial<ListItemProps<AccountOption>> = useMemo(
    () => ({
      listItemStyle: styles.listItem,
      listTitleStyle: styles.listTitle,
      renderListItemHeader: (option) =>
        option.metadata?.isSelected ? (
          <View
            style={styles.selectedLocationBackground}
            testID="location-picker-selected-location"
          />
        ) : null,
      subtitleMaxLines: 1,
    }),
    [styles],
  );

  useEffect(() => {
    if (!listRef.current || scrolledToSelectedIndexRef.current) {
      return;
    }

    const selectedIndex = sectionData.findIndex(
      (r) => r.value === activeRestaurantUUID,
    );
    if (selectedIndex !== -1) {
      InteractionManager.runAfterInteractions(() => {
        listRef.current?.scrollToIndex({
          animated: false,
          index: selectedIndex,
        });
        scrolledToSelectedIndexRef.current = true;
      });
    }
  }, [sectionData, activeRestaurantUUID]);

  return (
    <>
      <View style={styles.searchContainer}>
        <SearchInput
          autoFocus
          containerStyle={styles.searchInput}
          onChangeText={onDisplayTextChange}
          placeholder={i18n.t('search')}
          testID="location-picker-input"
          value={searchText}
        />
      </View>
      {searching ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" />
        </View>
      ) : (
        <RecyclerList
          contentContainerStyle={styles.recyclerContent}
          estimatedItemSize={72}
          keyboardDismissMode={Platform.OS === 'web' ? 'none' : 'on-drag'}
          keyboardShouldPersistTaps="handled"
          ListFooterComponent={
            sectionData.length === 0 ? (
              <EmptyStateMinimal
                containerStyle={styles.emptyState}
                iconProps={{
                  accentFill: theme.colors.accent,
                  fill: theme.colors.primary,
                }}
                ImageComponent={SnapPeas}
                text={i18n.t('oh_snap_peas')}
                textStyle={[TYPOGRAPHY.h2, styles.emptyStateTitle]}
              >
                <Text style={TYPOGRAPHY.medium}>
                  {i18n.t('no_results_found')}
                </Text>
              </EmptyStateMinimal>
            ) : null
          }
          listItemProps={listItemProps}
          listItemSeparatorProps={{
            style: styles.listItemSeparator,
          }}
          listRef={listRef}
          onSelect={(option) => {
            const restaurantUUID = String(option.value);
            Analytics.track('location_picker.select_account', {
              restaurantUUID,
            });
            onSelect(restaurantUUIDToRelationshipMap[restaurantUUID]);
          }}
          sections={[
            {
              data: sectionData,
              key: null,
            },
          ]}
          showSeparator
          skipInteractionCheck={skipInteractionCheck}
        />
      )}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  emptyState: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    flex: 1,
    justifyContent: 'center',
    minHeight: 496,
  },
  emptyStateTitle: {
    marginBottom: 8,
  },
  listItem: {
    marginHorizontal: 0,
    paddingLeft: SCREEN_PADDING,
    paddingRight: SCREEN_PADDING,
  },
  listItemSeparator: {
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  listTitle: {
    fontSize: theme.fontSizes.large,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 472,
  },
  recyclerContent: {
    backgroundColor: theme.colors.white,
  },
  searchContainer: {
    paddingBottom: 8,
    paddingHorizontal: 16,
  },
  searchInput: {
    backgroundColor: theme.colors.background,
  },
  selectedLocationBackground: {
    backgroundColor: theme.sk.color.bg.primary.active,
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
}));
